import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useSearch, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { authApi } from '../../api/endpoints/auth';
import { identifierValueSchema } from '../../lib/identifier';
import { useAuthStore } from '../../stores/authStore';
import { useLoginFlowStore } from '../../stores/loginFlowStore';
import { notifyApiError } from '../../api/errorMapping';
import { ApiError } from '../../api/errors';
import type { LoginRequestLocal, LoginResponseLocal } from '../../api/types/AuthLocal';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { TextField } from '../ui/TextField';
import { LoadingButton } from '../ui/LoadingButton';
import { Button } from '../ui/button';

// DEC-RBAC-080: el paso 1 acepta email O teléfono E.164 vía `identifier`. El campo del store
// sigue llamándose `email` (decisión v1: mínimo blast radius; lo leen MfaChallengeForm/forgot).
// Sólo cambia aquí el label, la validación y el body que se envía (`{ identifier }`).
const identifierSchema = z.object({ identifier: identifierValueSchema });
type IdentifierFormValues = z.infer<typeof identifierSchema>;

const passwordSchema = z.object({
    password: z.string().min(1),
});
type PasswordFormValues = z.infer<typeof passwordSchema>;

/**
 * Login email-first en 2 pasos (DEC-032, supersede A′):
 *  - Paso EMAIL: el usuario ingresa su correo → `resolveTenants` resuelve sus tenants.
 *      0 tenants → mensaje genérico (anti-enumeración, no revelamos nada).
 *      1 tenant  → pasa directo al paso PASSWORD con ese tenant.
 *      2+ tenants → paso PICK (elige tenant) → paso PASSWORD.
 *  - Paso PASSWORD: ingresa la contraseña → `login({ email, password, tenantId })` → challenge MFA.
 *
 * DEC-RBAC-055: el estado del flujo vive en `useLoginFlowStore` (Zustand + persist sessionStorage),
 * NO en `useState` local. Eso permite (a) "atrás" entre rutas sin reiniciar (al volver desde
 * /login/mfa-challenge o /login/new-password el flujo se resume en el paso password con
 * email/tenant ya puestos), (b) sobrevivir un refresh, (c) sacar el `session` de Cognito de la URL.
 *
 * El "remember-tenant" del spec se omite a propósito (la regla de lint del repo prohíbe
 * localStorage directo y la mitigación del DoS es el throttle backend, no recordar en cliente).
 */
export function LoginForm() {
    const { t } = useTranslation(['auth']);
    const navigate = useNavigate();
    const search: { from?: string } = useSearch({ strict: false });
    const fromPath = search.from;
    const refresh = useAuthStore((s) => s.refresh);

    // Estado del flujo desde el store persistido (resume el paso correcto al montar/refrescar).
    const step = useLoginFlowStore((s) => s.step);
    const email = useLoginFlowStore((s) => s.email);
    const tenantId = useLoginFlowStore((s) => s.tenantId);
    const tenantName = useLoginFlowStore((s) => s.tenantName);
    const tenants = useLoginFlowStore((s) => s.tenants);
    const setStoreEmail = useLoginFlowStore((s) => s.setEmail);
    const setTenants = useLoginFlowStore((s) => s.setTenants);
    const pickTenantAction = useLoginFlowStore((s) => s.pickTenant);
    const setChallenge = useLoginFlowStore((s) => s.setChallenge);
    const back = useLoginFlowStore((s) => s.back);
    const reset = useLoginFlowStore((s) => s.reset);

    const identifierForm = useForm<IdentifierFormValues>({
        resolver: zodResolver(identifierSchema),
        defaultValues: { identifier: email },
    });
    const passwordForm = useForm<PasswordFormValues>({
        resolver: zodResolver(passwordSchema),
        defaultValues: { password: '' },
    });

    // Si el flujo está en un paso de challenge (mfa/new-password) y el usuario aterriza en /login
    // (ej. apretó "atrás" del navegador), lo mandamos a la ruta del challenge correspondiente para
    // no mostrar el login a medias. El "atrás" controlado vive en las pantallas de challenge.
    useEffect(() => {
        if (step === 'mfa') {
            void navigate({ to: '/login/mfa-challenge' });
        } else if (step === 'new-password') {
            void navigate({ to: '/login/new-password' });
        }
    }, [step, navigate]);

    // Warm-up: al abrir el login despertamos la lambda de auth. El correo OTP se encola
    // dentro del propio `login` (send-and-forget), pero la respuesta trae el `session` que
    // necesitamos para verificar el código — si el request muere por cold-start nos quedamos
    // sin él. Calentar antes de que el usuario envíe minimiza ese caso.
    useEffect(() => {
        void authApi.healthcheck().catch(() => {
            /* best-effort, los errores del warm-up no importan */
        });
    }, []);

    // Paso 1: resolver los tenants del identificador (email o teléfono — DEC-RBAC-080).
    // TD: el backend `resolve-tenants` aún lee `email` (el openapi no acepta `identifier`,
    // a diferencia de reset-password). Mandamos AMBOS como puente: funciona contra el backend
    // actual (email) y contra el fix futuro (identifier). Quitar `email` cuando el rbac acepte identifier.
    const resolveMutation = useMutation({
        mutationFn: (id: string) => authApi.resolveTenants({ identifier: id, email: id }),
        onSuccess: (resp) => {
            if (resp.tenants.length === 0) {
                // Anti-enumeración: no revelamos si el email existe o no.
                toast.error('Revisa tus datos e intenta de nuevo.');
                return;
            }
            // El store decide el paso (1 → password, 2+ → pick) y guarda los candidatos.
            setTenants(resp.tenants);
        },
        onError: () => {
            toast.error('No pudimos continuar. Intenta de nuevo.');
        },
    });

    // Paso 2: login contra el pool del tenant elegido → challenge MFA.
    const loginMutation = useMutation({
        mutationFn: (values: LoginRequestLocal) => authApi.login(values),
        onSuccess: async (resp: LoginResponseLocal, variables) => {
            // Si no hay challenge → tokens emitidos directo (CUSTOM_AUTH no debería pasar por aquí hoy).
            if (!resp.challenge) {
                reset();
                await refresh();
                await navigate({ to: fromPath ?? '/' });
                return;
            }

            // Cognito CUSTOM_AUTH siempre devuelve 'CUSTOM_CHALLENGE' como challengeType.
            // El sub-challenge real (EMAIL_OTP/TOTP/etc.) lo mapeó el backend en PublicController.login
            // basado en el mfaMethod del user. Si el backend devuelve 'CUSTOM_CHALLENGE' literal,
            // asumimos EMAIL_OTP por hoy (único sub-challenge soportado).
            const subChallenge =
                resp.challenge.challengeType === 'CUSTOM_CHALLENGE'
                    ? 'EMAIL_OTP'
                    : resp.challenge.challengeType;

            // El session/email/tenant viajan por el store (DEC-RBAC-055), ya no por la URL.
            switch (subChallenge) {
                case 'NEW_PASSWORD_REQUIRED':
                    setChallenge({
                        session: resp.challenge.session,
                        challengeType: 'NEW_PASSWORD_REQUIRED',
                    });
                    await navigate({ to: '/login/new-password' });
                    break;
                case 'SOFTWARE_TOKEN_MFA':
                    setChallenge({
                        session: resp.challenge.session,
                        challengeType: 'SOFTWARE_TOKEN_MFA',
                    });
                    await navigate({ to: '/login/mfa-challenge' });
                    break;
                case 'EMAIL_OTP':
                    setChallenge({ session: resp.challenge.session, challengeType: 'EMAIL_OTP' });
                    await navigate({ to: '/login/mfa-challenge' });
                    break;
                // DEC-RBAC-084: el user con SMS como 2º factor recibe el código por SMS y lo ingresa
                // en la misma pantalla de challenge (mismo ruteo que EMAIL_OTP).
                case 'SMS_OTP':
                    setChallenge({ session: resp.challenge.session, challengeType: 'SMS_OTP' });
                    await navigate({ to: '/login/mfa-challenge' });
                    break;
                // DEC-047: el login ya NO devuelve 'MFA_SETUP'. Un user sin 2º factor entra por
                // EMAIL_OTP (arriba) → obtiene tokens → el guard del front lo fuerza a la pantalla
                // standalone de elección post-token. La elección no se rutea desde aquí.
                default:
                    // Tipo no esperado: dejamos `variables` sin usar explícito (lint) y avisamos.
                    void variables;
                    toast.error('No pudimos continuar con el segundo factor. Intenta de nuevo.');
            }
        },
        onError: (error: unknown) => {
            // Cold-start: en frío el gateway corta con 502/503/504 (sin `code` Fiado) aunque el
            // correo OTP ya se haya encolado. No podemos seguir sin el `session` de la respuesta,
            // así que pedimos reintentar. (Va antes del mapeo genérico.)
            if (error instanceof ApiError && error.httpStatus >= 502) {
                toast.error(
                    'El servicio tardó de más al despertar. Si ya te llegó el correo con el código, espera unos segundos y vuelve a intentar.',
                );
                return;
            }
            // Credenciales inválidas: el backend devuelve 401 INVALID_CREDENTIALS, pero como es un
            // 401 lo trataríamos como sesión expirada; aquí lo interceptamos antes para mostrar el
            // toast correcto en vez de redirigir.
            if (error instanceof ApiError && error.code === 'INVALID_CREDENTIALS') {
                toast.error(t('auth:errors.invalidCredentials'));
                return;
            }
            if (error instanceof ApiError && error.code === 'VALIDATION_ERROR') {
                const fields =
                    (error.data as { fields?: Record<string, string> } | undefined)?.fields ?? {};
                for (const [k, msg] of Object.entries(fields)) {
                    if (k === 'password') passwordForm.setError(k, { message: msg });
                }
                toast.error('Revisa los datos e intenta de nuevo.');
                return;
            }
            // Resto (red, timeout, codes no mapeados): un solo aviso, mapeado por code.
            notifyApiError(error);
        },
    });

    const submitPassword = (values: PasswordFormValues): void => {
        if (step !== 'password' || !tenantId) return;
        // Guard anti doble-disparo: el `disabled={isPending}` del LoadingButton cubre el caso
        // estable, pero hay una ventana de carrera entre el primer click y el re-render que
        // marca isPending=true. Sin esto, un doble-submit rápido cuela un 2º POST /auth/login
        // (InitiateAuth) que genera un 2º OTP por correo. (FIX doble-request MFA/login.)
        if (loginMutation.isPending) return;
        // DEC-RBAC-080: el body manda `identifier` (el valor del paso 1, email o teléfono). El store
        // lo guarda en `email` por compat interna; el backend resuelve por `identifier ?? email`.
        loginMutation.mutate({ identifier: email, password: values.password, tenantId });
    };

    // "Atrás": transición del store preservando lo cargado. Reseteamos el form de password porque
    // la contraseña no se persiste (security). El email queda en el store y se rehidrata.
    const goBack = (): void => {
        passwordForm.reset();
        back();
    };

    return (
        <Card className="w-full max-w-md shadow-lg">
            <CardHeader className="space-y-1 text-center">
                <CardTitle className="text-2xl">{t('auth:login.title')}</CardTitle>
                <CardDescription>
                    Ingresa tus credenciales para acceder al backoffice.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {step === 'email' && (
                    <form
                        onSubmit={(e) => {
                            void identifierForm.handleSubmit((v) => {
                                setStoreEmail(v.identifier);
                                resolveMutation.mutate(v.identifier);
                            })(e);
                        }}
                        className="space-y-4"
                        noValidate
                    >
                        <TextField
                            label={t('auth:login.identifier')}
                            type="text"
                            autoComplete="username"
                            placeholder="tu@email.com o +521234567890"
                            disabled={resolveMutation.isPending}
                            error={identifierForm.formState.errors.identifier?.message}
                            {...identifierForm.register('identifier')}
                        />
                        <LoadingButton
                            type="submit"
                            className="w-full"
                            isLoading={resolveMutation.isPending}
                            loadingText="Buscando…"
                        >
                            Continuar
                        </LoadingButton>
                        <div className="space-y-1 text-center text-sm">
                            <div>
                                <Link
                                    to="/forgot-password"
                                    className="text-brand-accent hover:underline"
                                >
                                    {t('auth:login.forgotPassword')}
                                </Link>
                            </div>
                            <div className="text-muted-foreground">
                                ¿No tienes cuenta?{' '}
                                <Link
                                    to="/self-register"
                                    className="text-brand-accent hover:underline"
                                >
                                    Regístrate
                                </Link>
                            </div>
                        </div>
                    </form>
                )}

                {step === 'pick' && (
                    <div className="space-y-3">
                        <p className="text-sm">
                            Tienes acceso a varias organizaciones con este correo. ¿A cuál quieres
                            entrar?
                        </p>
                        {tenants.map((tnt) => (
                            <Button
                                key={tnt.tenantId}
                                type="button"
                                variant="outline"
                                className="w-full justify-start hover:bg-muted hover:text-foreground"
                                onClick={() => {
                                    pickTenantAction(tnt);
                                }}
                            >
                                {tnt.displayName}
                            </Button>
                        ))}
                        <Button
                            type="button"
                            variant="ghost"
                            className="w-full text-sm hover:bg-muted hover:text-foreground"
                            onClick={goBack}
                        >
                            <ArrowLeft className="mr-1 h-4 w-4" aria-hidden />
                            Cambiar correo
                        </Button>
                    </div>
                )}

                {step === 'password' && (
                    <form
                        onSubmit={(e) => {
                            void passwordForm.handleSubmit(submitPassword)(e);
                        }}
                        className="space-y-4"
                        noValidate
                    >
                        <p className="text-sm text-muted-foreground">
                            Ingresando a <span className="font-medium">{tenantName}</span>
                        </p>
                        <TextField
                            label={t('auth:login.password')}
                            type="password"
                            autoComplete="current-password"
                            error={passwordForm.formState.errors.password?.message}
                            {...passwordForm.register('password')}
                        />
                        <LoadingButton
                            type="submit"
                            className="w-full"
                            isLoading={loginMutation.isPending}
                            loadingText="Validando…"
                        >
                            {t('auth:login.submit')}
                        </LoadingButton>
                        <Button
                            type="button"
                            variant="ghost"
                            className="w-full text-sm hover:bg-muted hover:text-foreground"
                            onClick={goBack}
                        >
                            <ArrowLeft className="mr-1 h-4 w-4" aria-hidden />
                            {tenants.length >= 2 ? 'Cambiar organización' : 'Cambiar correo'}
                        </Button>
                        <div className="text-center text-sm">
                            <Link
                                to="/forgot-password"
                                className="text-brand-accent hover:underline"
                            >
                                {t('auth:login.forgotPassword')}
                            </Link>
                        </div>
                    </form>
                )}
            </CardContent>
        </Card>
    );
}
